/**
 * AssetKindRegistry — concrete implementation of IAssetKindRegistry.
 *
 * Frozen-state machine:
 * - Created in "open" state, accepting provider registrations
 * - First read operation (or explicit freeze()) transitions to "frozen"
 * - No further registrations after freeze
 *
 * Pattern conflict detection:
 * - On register(), checks that the new provider's discoveryPatterns don't
 *   overlap with any existing provider's patterns (exact string match).
 * - At resolveKind() time, checks that only one provider's matchesPath()
 *   returns true (runtime safety net).
 *
 * Design decisions A3, A4: locked in Phase 1.7 planning.
 */

import type {
  AssetKindFileFilter,
  AssetKindRequiresEdge,
  IAssetKindProvider,
  IAssetKindRegistry,
} from "@skaile/workspaces/plugins";
import {
  AssetKindConflictError,
  AssetKindPathAmbiguityError,
  AssetKindPatternConflictError,
  AssetKindRegistryFrozenError,
} from "@skaile/workspaces/plugins";
import type { ManifestValidationResult } from "@skaile/workspaces/types/manifests";

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class AssetKindRegistry implements IAssetKindRegistry {
  private providers = new Map<string, IAssetKindProvider>();
  private patternOwnership = new Map<string, string>(); // pattern -> kind
  private _frozen = false;

  get frozen(): boolean {
    return this._frozen;
  }

  // -------------------------------------------------------------------------
  // Registration (open state only)
  // -------------------------------------------------------------------------

  register(provider: IAssetKindProvider): void {
    if (this._frozen) {
      throw new AssetKindRegistryFrozenError();
    }

    // A4: kind conflict
    if (this.providers.has(provider.kind)) {
      throw new AssetKindConflictError(provider.kind);
    }

    // A4: pattern conflict — check each discovery pattern against all registered
    for (const pattern of provider.discoveryPatterns) {
      const existingOwner = this.patternOwnership.get(pattern);
      if (existingOwner) {
        throw new AssetKindPatternConflictError(provider.kind, existingOwner, pattern);
      }
    }

    // Commit registration
    this.providers.set(provider.kind, provider);
    for (const pattern of provider.discoveryPatterns) {
      this.patternOwnership.set(pattern, provider.kind);
    }
  }

  // -------------------------------------------------------------------------
  // Freeze
  // -------------------------------------------------------------------------

  freeze(): void {
    this._frozen = true;
  }

  // -------------------------------------------------------------------------
  // Read operations (auto-freeze)
  // -------------------------------------------------------------------------

  getProvider(kind: string): IAssetKindProvider | undefined {
    this.autoFreeze();
    return this.providers.get(kind);
  }

  getAllProviders(): IAssetKindProvider[] {
    this.autoFreeze();
    return [...this.providers.values()];
  }

  getAllKinds(): string[] {
    this.autoFreeze();
    return [...this.providers.keys()];
  }

  resolveKind(relativePath: string): string | undefined {
    this.autoFreeze();

    const matches: string[] = [];
    for (const provider of this.providers.values()) {
      if (provider.matchesPath(relativePath)) {
        matches.push(provider.kind);
      }
    }

    if (matches.length === 0) return undefined;
    if (matches.length === 1) return matches[0];

    // A4: multiple providers claim the same path — hard error
    throw new AssetKindPathAmbiguityError(relativePath, matches);
  }

  // -------------------------------------------------------------------------
  // Convenience delegates
  // -------------------------------------------------------------------------

  validateManifest(kind: string, data: unknown): ManifestValidationResult<unknown> {
    this.autoFreeze();
    const provider = this.providers.get(kind);
    if (!provider) {
      return {
        ok: false,
        errors: [
          {
            path: "kind",
            message: `No provider registered for kind "${kind}"`,
          },
        ],
      };
    }
    return provider.validateManifest(data);
  }

  defaultFileFilter(kind: string, manifestPath: string): AssetKindFileFilter | undefined {
    this.autoFreeze();
    const provider = this.providers.get(kind);
    if (!provider) return undefined;
    return provider.defaultFileFilter(manifestPath);
  }

  extractRequires(
    kind: string,
    assetRef: string,
    manifest: Record<string, unknown>,
  ): AssetKindRequiresEdge[] {
    this.autoFreeze();
    const provider = this.providers.get(kind);
    if (!provider?.extractRequires) return [];
    return provider.extractRequires(assetRef, manifest);
  }

  // -------------------------------------------------------------------------
  // Internal
  // -------------------------------------------------------------------------

  private autoFreeze(): void {
    if (!this._frozen) {
      this._frozen = true;
    }
  }
}
